import { useMemo, useState } from 'react';
import { stateLocations } from '../propertyConfig';

const defaultFormFlags = {
  furnished: false,
  serviced: false,
  selfContained: false,
  newDevelopment: false,
  parking: false,
  gated: false,
  boysQuarters: false,
  prepaidMeter: false,
  borehole: false,
  studentFriendly: false,
  noAgencyFee: false,
  nearSchool: false,
  nearMarket: false,
  nearExpressway: false,
  floodFree: false,
};

export function usePropertyForm() {
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [area, setArea] = useState('');
  const [listingType, setListingType] = useState('rent');
  const [propertyType, setPropertyType] = useState('Apartment');
  const [customPropertyType, setCustomPropertyType] = useState('');
  const [sellerType, setSellerType] = useState('owner');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentTermsMonths, setPaymentTermsMonths] = useState('');
  const [paymentTermsUnit, setPaymentTermsUnit] = useState('months'); // NEW: Track selected unit
  const [tenancyType, setTenancyType] = useState('new-let');
  const [serviceCharge, setServiceCharge] = useState('');
  const [cautionFee, setCautionFee] = useState('');
  const [agencyFee, setAgencyFee] = useState('');
  const [legalFee, setLegalFee] = useState('');
  const [agreementDurationMonths, setAgreementDurationMonths] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [occupancyStatus, setOccupancyStatus] = useState('vacant');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [description, setDescription] = useState('');
  const [photoUrls, setPhotoUrls] = useState(Array(10).fill(''));
  const [agencyName, setAgencyName] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [formFlags, setFormFlags] = useState(defaultFormFlags);

  // Lease duration for rent listings
  const [leaseDuration, setLeaseDuration] = useState('');
  const [leaseDurationMonths, setLeaseDurationMonths] = useState('');
  const [leaseDurationUnit, setLeaseDurationUnit] = useState('months'); // NEW: Track selected unit

  // Short-let fields
  const [pricePerNight, setPricePerNight] = useState('');
  const [minNights, setMinNights] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [availabilityStart, setAvailabilityStart] = useState('');
  const [availabilityEnd, setAvailabilityEnd] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [houseRules, setHouseRules] = useState('');
  const [damageDepositAmount, setDamageDepositAmount] = useState('');
  const [damageDepositNotes, setDamageDepositNotes] = useState('');

  // Rent/buy only - informational open-viewing windows, array of
  // {day, startTime, endTime}.
  const [openViewingSchedule, setOpenViewingSchedule] = useState([]);

  // For Sale only - advertiser-declared documents (see PROPERTY_DOCUMENT_TYPES
  // in propertyConfig.js). Never verified by NaijaNest.
  const [propertyDocuments, setPropertyDocuments] = useState([]);
  const [propertyDocumentsOther, setPropertyDocumentsOther] = useState('');

  const addFormAreas = location ? stateLocations[location] || [] : [];

  const resetForm = () => {
    setTitle('');
    setPrice('');
    setLocation('');
    setArea('');
    setLatitude('');
    setLongitude('');
    setAddress('');
    setListingType('rent');
    setPropertyType('Apartment');
    setCustomPropertyType('');
    setSellerType('owner');
    setPaymentTerms('yearly');
    setPaymentTermsMonths('');
    setPaymentTermsUnit('months'); // NEW: Reset unit to default
    setTenancyType('new-let');
    setServiceCharge('');
    setCautionFee('');
    setAgencyFee('');
    setLegalFee('');
    setAgreementDurationMonths('');
    setAvailableFrom('');
    setOccupancyStatus('vacant');
    setOwnerPhone('');
    setDescription('');
    setPhotoUrls(Array(10).fill(''));
    setAgencyName('');
    setBedrooms('');
    setBathrooms('');
    setFormFlags(defaultFormFlags);
    setLeaseDuration('');
    setLeaseDurationMonths('');
    setLeaseDurationUnit('months'); // NEW: Reset unit to default
    setPricePerNight('');
    setMinNights('');
    setMaxGuests('');
    setAvailabilityStart('');
    setAvailabilityEnd('');
    setAmenities([]);
    setWhatsappNumber('');
    setCheckInTime('');
    setCheckOutTime('');
    setHouseRules('');
    setDamageDepositAmount('');
    setDamageDepositNotes('');
    setOpenViewingSchedule([]);
    setPropertyDocuments([]);
    setPropertyDocumentsOther('');
  };

  const formSetters = useMemo(
    () => ({
      setTitle,
      setPrice,
      setLocation,
      setArea,
      setListingType,
      setPropertyType,
      setCustomPropertyType,
      setSellerType,
      setPaymentTerms,
      setPaymentTermsMonths,
      setPaymentTermsUnit, // NEW: Track selected unit
      setTenancyType,
      setServiceCharge,
      setCautionFee,
      setAgencyFee,
      setLegalFee,
      setAgreementDurationMonths,
      setAvailableFrom,
      setOccupancyStatus,
      setOwnerPhone,
      setDescription,
      setPhotoUrls,
      setPhotoAtIndex: (index, value) => {
        const normalizedIndex = Number(index);
        if (Number.isNaN(normalizedIndex) || normalizedIndex < 0 || normalizedIndex > 9) {
          return;
        }
        setPhotoUrls((current) => {
          const next = [...current];
          next[normalizedIndex] = value;
          return next;
        });
      },
      setCoverPhotoIndex: (index) => {
        const normalizedIndex = Number(index);
        if (Number.isNaN(normalizedIndex) || normalizedIndex < 0 || normalizedIndex > 9) {
          return;
        }
        setPhotoUrls((current) => {
          const next = [...current];
          if (!next[normalizedIndex]) {
            return current;
          }
          const [selected] = next.splice(normalizedIndex, 1);
          next.unshift(selected);
          return next.slice(0, 10);
        });
      },
      movePhoto: (fromIndex, toIndex) => {
        const sourceIndex = Number(fromIndex);
        const targetIndex = Number(toIndex);
        if (
          Number.isNaN(sourceIndex) ||
          Number.isNaN(targetIndex) ||
          sourceIndex < 0 ||
          sourceIndex > 9 ||
          targetIndex < 0 ||
          targetIndex > 9 ||
          sourceIndex === targetIndex
        ) {
          return;
        }

        setPhotoUrls((current) => {
          const next = [...current];
          const [selected] = next.splice(sourceIndex, 1);
          next.splice(targetIndex, 0, selected || '');
          return next.slice(0, 10);
        });
      },
      setAgencyName,
      setBedrooms,
      setBathrooms,
      setLeaseDuration,
      setLeaseDurationMonths,
      setLeaseDurationUnit, // NEW: Track selected unit
      setPricePerNight,
      setMinNights,
      setMaxGuests,
      setAvailabilityStart,
      setAvailabilityEnd,
      setAmenities,
      setWhatsappNumber,
      setCheckInTime,
      setCheckOutTime,
      setHouseRules,
      setDamageDepositAmount,
      setDamageDepositNotes,
      setOpenViewingSchedule,
      setPropertyDocuments,
      setPropertyDocumentsOther,
      setLatitude,
      setLongitude,
      setAddress,
      ...Object.fromEntries(
        Object.keys(defaultFormFlags).map((key) => [
          key,
          (value) => setFormFlags((current) => ({ ...current, [key]: value })),
        ])
      ),
    }),
    []
  );

  const formState = {
    title,
    price,
    location,
    area,
    listingType,
    propertyType,
    customPropertyType,
    sellerType,
    paymentTerms,
    paymentTermsMonths,
    paymentTermsUnit, // NEW: Track selected unit
    tenancyType,
    serviceCharge,
    cautionFee,
    agencyFee,
    legalFee,
    agreementDurationMonths,
    availableFrom,
    occupancyStatus,
    ownerPhone,
    description,
    photoUrls,
    agencyName,
    bedrooms,
    bathrooms,
    latitude,
    longitude,
    address,
    leaseDuration,
    leaseDurationMonths,
    leaseDurationUnit, // NEW: Track selected unit
    pricePerNight,
    minNights,
    maxGuests,
    availabilityStart,
    availabilityEnd,
    amenities,
    whatsappNumber,
    checkInTime,
    checkOutTime,
    houseRules,
    damageDepositAmount,
    damageDepositNotes,
    openViewingSchedule,
    propertyDocuments,
    propertyDocumentsOther,
    ...formFlags,
  };

  const buildPayload = () => {
    const normalizedPhotos = (photoUrls || []).map((item) => String(item || '').trim());
    const images = [...new Set(normalizedPhotos.filter(Boolean))].slice(0, 10);
    const image = images[0] || '';

    // Resolve property type — use custom label when 'Other' is selected
    const resolvedPropertyType =
      propertyType === 'Other' && customPropertyType.trim()
        ? customPropertyType.trim()
        : propertyType;

    // AddressAutocomplete only sets latitude/longitude when the user picks a
    // suggestion from its search results - a very common miss for informal
    // or newly-built Nigerian addresses (Google's predictive search has no
    // record of them yet), or if the user just types and moves on without
    // selecting one. When that happens, the server resolves a real location
    // itself from the address/area/state text (street -> area/LCDA -> state
    // cascade in server/geocode.js) rather than this form guessing - so
    // latitude/longitude are only sent here when Places actually resolved
    // them.
    const basePayload = {
      title,
      location: address ? address.trim() : area ? `${area}, ${location}` : location,
      state: location,
      area,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      listingType,
      propertyType: resolvedPropertyType,
      sellerType,
      description: String(description || '')
        .trim()
        .slice(0, 1000),
      image,
      images,
      ownerPhone,
      agencyName,
      bedrooms,
      bathrooms,
      ...formFlags,
    };

    // Type-specific fields
    let typeSpecificFields = {};

    if (listingType === 'rent') {
      typeSpecificFields = {
        price,
        leaseDuration: leaseDuration || undefined,
        leaseDurationMonths:
          leaseDuration === 'custom' && leaseDurationMonths
            ? Number(leaseDurationMonths)
            : undefined,
        paymentTerms,
        tenancyType,
        serviceCharge,
        cautionFee,
        agencyFee,
        legalFee,
        agreementDurationMonths,
        availableFrom,
        occupancyStatus,
        openViewingSchedule: openViewingSchedule.length ? openViewingSchedule : undefined,
      };
    } else if (listingType === 'short-let') {
      const normalizedShortLetPrice = pricePerNight
        ? Number(String(pricePerNight).replace(/,/g, ''))
        : undefined;
      typeSpecificFields = {
        price: normalizedShortLetPrice,
        pricePerNight: normalizedShortLetPrice,
        minNights: minNights ? Number(minNights) : undefined,
        maxGuests: maxGuests ? Number(maxGuests) : undefined,
        availabilityStart: availabilityStart || undefined,
        availabilityEnd: availabilityEnd || undefined,
        availableFrom: availableFrom || undefined,
        amenities: amenities.length ? amenities : undefined,
        whatsappNumber: whatsappNumber || undefined,
        checkInTime: checkInTime || undefined,
        checkOutTime: checkOutTime || undefined,
        houseRules: houseRules ? houseRules.trim().slice(0, 1000) : undefined,
        damageDepositAmount: damageDepositAmount ? Number(damageDepositAmount) : undefined,
        damageDepositNotes: damageDepositNotes ? damageDepositNotes.trim().slice(0, 300) : undefined,
      };
    } else if (listingType === 'buy') {
      typeSpecificFields = {
        price,
        paymentTerms: paymentTerms || undefined,
        paymentTermsMonths:
          paymentTerms === 'custom' && paymentTermsMonths ? Number(paymentTermsMonths) : undefined,
        openViewingSchedule: openViewingSchedule.length ? openViewingSchedule : undefined,
        occupancyStatus,
        serviceCharge,
        agencyFee,
        legalFee,
        // Only sent for For Sale listings - if an edit switches a listing's
        // type away from 'buy', this key is simply absent from the payload,
        // so PATCH /properties/:id's val() helper keeps whatever was
        // previously saved instead of wiping it (see server comment there).
        propertyDocuments: propertyDocuments.length ? propertyDocuments : undefined,
        propertyDocumentsOther: propertyDocuments.includes('other')
          ? propertyDocumentsOther.trim().slice(0, 100) || undefined
          : undefined,
      };
    }

    return {
      ...basePayload,
      ...typeSpecificFields,
    };
  };

  return {
    formState,
    formSetters,
    addFormAreas,
    buildPayload,
    resetForm,
  };
}
